import React, { useState, useEffect } from 'react';
import { Modal, Button, Space, Typography, List, Spin } from 'antd';
import { FolderOutlined, RightOutlined, HomeOutlined } from '@ant-design/icons';
import { PostFolder } from '../../../../../../../types/postFolders';
import { PostFolderService } from '../../../../../../../services/PostFolderService';
import { genericWriteAccessHandler } from '../../../../../../../components/Profile/components/ProfileMenu/helpers';
import './style.css';

const { Text } = Typography;

interface MovePostModalProps {
  open: boolean;
  spaceId: string;
  spaceName: string;
  currentFolderId: string | null;
  onCancel: () => void;
  onSubmit: (folderId: string | null, path: FolderPath[]) => Promise<void>;
  user: any;
}

interface FolderPath {
  id: string;
  name: string;
}

const MovePostModal: React.FC<MovePostModalProps> = ({
  open,
  spaceId,
  spaceName,
  currentFolderId,
  onCancel,
  onSubmit,
  user
}) => {
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<PostFolder[]>([]);
  const [currentPath, setCurrentPath] = useState<FolderPath[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<PostFolder | null>(null);

  const loadFolders = async (parentId?: string) => {
    try {
      setLoading(true);
      const service = new PostFolderService(
        `space/${spaceId}/posts/folders/list${parentId ? `?parent_folder_id=${parentId}` : ''}`
      );
      const folderList = await service.listFolders();
      setFolders(folderList as PostFolder[]);
    } catch (error) {
      // Error handling will be done by the parent
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = async (folder: PostFolder | null) => {
    if (!folder) {
      // Navigate to root
      setCurrentPath([]);
      await loadFolders();
      setSelectedFolderId(null);
      setSelectedFolder(null);
      return;
    }

    try {
      setLoading(true);
      await loadFolders(folder.id);
      if (currentPath.length > 0) {
        const folderIndex = currentPath.findIndex(f => f.id === folder.id);
        if (folderIndex !== -1) {
          // Keep only the path up to and including the clicked folder
          setCurrentPath(currentPath.slice(0, folderIndex + 1));
        } else {
          // If folder not in path, append it
          setCurrentPath([...currentPath, folder]);
        }
      } else {
        // First folder selection
        setCurrentPath([folder]);
      }
      setSelectedFolderId(folder.id);
      setSelectedFolder(folder);
    } catch (error) {
      // Error handling will be done by the parent
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadFolders(currentPath[currentPath.length - 1]?.id);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!canMoveToFolder()) {
      return;
    }

    try {
      setLoading(true);
      await onSubmit(selectedFolderId, currentPath);
      onCancel();
    } catch (error) {
      // Error handling is done by parent component
    } finally {
      setLoading(false);
    }
  };

  const renderBreadcrumb = () => (
    <div className="folder-breadcrumb">
      <Button 
        type="text" 
        icon={<HomeOutlined />} 
        onClick={() => navigateToFolder(null)}
        className={currentPath.length === 0 ? 'active' : ''}
      >
        {spaceName}
      </Button>
      {currentPath.map((folder, index) => (
        <React.Fragment key={folder.id}>
          <RightOutlined className="separator" />
          <Button
            type="text"
            onClick={() => {
              navigateToFolder(folder as PostFolder);
            }}
            className={index === currentPath.length - 1 ? 'active' : ''}
          >
            {folder.name}
          </Button>
        </React.Fragment>
      ))}
    </div>
  );

  const canMoveToRoot = () => {
    return genericWriteAccessHandler(user, spaceId, undefined, false);
  };

  const canMoveToFolder = () => {
    // If no folder is selected, check root access
    if (!selectedFolder) {
      return canMoveToRoot();
    }
    
    // Check admin permissions of the selected folder
    return selectedFolder.metadata?.permissions?.admin || false;
  };

  return (
    <Modal
      title={
        <div className="modal-title">
          <div className="title-text">Move Post to Folder</div>
          {renderBreadcrumb()}
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          disabled={selectedFolderId === currentFolderId || !canMoveToFolder()}
          loading={loading}
        >
          Move Here
        </Button>
      ]}
      width={600}
      className="move-post-modal"
      centered
      maskClosable={false}
    >
      <Spin spinning={loading}>
        <div className="folder-list">
          {folders.length === 0 ? (
            <div className="empty-folder">
              <Text type="secondary">No folders in this location</Text>
            </div>
          ) : (
            <List
              dataSource={folders}
              renderItem={(folder) => (
                <List.Item
                  key={folder.id}
                  className={`folder-item ${selectedFolderId === folder.id ? 'selected' : ''}`}
                  onClick={() => navigateToFolder(folder)}
                >
                  <Space>
                    <FolderOutlined />
                    <span>{folder.name}</span>
                  </Space>
                </List.Item>
              )}
            />
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default MovePostModal; 